/**
 * Entitlements store for Idle Depths.
 *
 * Manages DLC entitlements and feature access.
 * Guests have base_game only; authenticated users may have purchases.
 */

import { ENTITLEMENTS, DEFAULT_ENTITLEMENTS, type EntitlementKey } from '@idle-depths/shared';
import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

import { getVanillaClient } from '@/lib/trpc';

/**
 * Entitlements store.
 *
 * Tracks which DLCs/features the user has access to.
 */
export class EntitlementsStore {
  /** Set of entitlement keys the user has */
  entitlements: Set<EntitlementKey> = new Set(DEFAULT_ENTITLEMENTS);

  /** Whether entitlements are loading */
  loading = false;

  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  /**
   * Initialize entitlements.
   *
   * Loads from server if authenticated, otherwise uses defaults.
   */
  async initialize(): Promise<void> {
    if (!this.rootStore.authStore.shouldSyncToServer) {
      // Guest - base game only
      runInAction(() => {
        this.entitlements = new Set(DEFAULT_ENTITLEMENTS);
      });
      return;
    }

    await this.loadFromServer();
  }

  /**
   * Load entitlements from server.
   */
  async loadFromServer(): Promise<void> {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const client = getVanillaClient();
      const result = await client.user.getEntitlements.query();

      runInAction(() => {
        this.entitlements = new Set([
          ENTITLEMENTS.BASE_GAME,
          ...(result.entitlements as EntitlementKey[]),
        ]);
      });
    } catch (error) {
      // On error, keep current entitlements (at minimum base_game)
      Sentry.captureException(error, {
        tags: { context: 'entitlements_load' },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Check if user has a specific entitlement.
   *
   * @param key - Entitlement key to check
   * @returns True if user has the entitlement
   */
  hasEntitlement(key: EntitlementKey): boolean {
    return this.entitlements.has(key);
  }

  /**
   * Whether user has premium unlock (all areas, skill trees, etc.).
   */
  get hasPremium(): boolean {
    return this.hasEntitlement(ENTITLEMENTS.PREMIUM_UNLOCK);
  }

  /**
   * Whether user has dev support pack (cosmetics, soundtrack, etc.).
   */
  get hasDevSupport(): boolean {
    return this.hasEntitlement(ENTITLEMENTS.DEV_SUPPORT);
  }

  /**
   * Whether user has convenience pack (auto-features).
   */
  get hasConvenience(): boolean {
    return this.hasEntitlement(ENTITLEMENTS.CONVENIENCE);
  }

  /**
   * Whether user has developer access (debug tools).
   * This is granted via direct database access only.
   */
  get isDeveloper(): boolean {
    return this.hasEntitlement(ENTITLEMENTS.DEV);
  }

  /**
   * Whether user should see ads (no premium = show ads).
   */
  get shouldShowAds(): boolean {
    return !this.hasPremium;
  }

  /**
   * Get list of all entitlement keys user has.
   */
  get entitlementList(): EntitlementKey[] {
    return Array.from(this.entitlements);
  }

  /**
   * Reset store to initial state.
   */
  reset(): void {
    runInAction(() => {
      this.entitlements = new Set(DEFAULT_ENTITLEMENTS);
      this.loading = false;
    });
  }
}
